import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

interface LoginModalProps {
  show: boolean;
}

// ===== Composant LoginModal =====
const LoginModal: React.FC<LoginModalProps> = ({ show }) => {

  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rateLimitInfo, setRateLimitInfo] = useState<{ blocked: boolean; retryAfter: number } | null>(null);
  const { login } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (rateLimitInfo && rateLimitInfo.retryAfter > 0) {
      const timer = setInterval(() => {
        setRateLimitInfo(prev => {
          if (!prev || prev.retryAfter <= 1) {
            return null;
          }
          return { ...prev, retryAfter: prev.retryAfter - 1 };
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [rateLimitInfo?.blocked]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) return;

    if (rateLimitInfo?.blocked) {
      return;
    }

    setIsLoading(true);
    try {
      await login(password);
      setPassword('');
      setRateLimitInfo(null);
    } catch (error: any) {

      if (error.response?.status === 429) {
        const retryAfter = error.response?.data?.retryAfter || 60;
        setRateLimitInfo({ blocked: true, retryAfter });
        showNotification('Trop de tentatives de connexion', 'error');
      } else {
        showNotification(error.message || error.response?.data?.error || 'Mot de passe incorrect', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal-backdrop">
      <div className="glass-effect modal-content">
        {}
        <div className="text-center mb-6">
          <i className="fas fa-lock text-blue-600 text-5xl mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800">Connexion LGV</h2>
          <p className="text-gray-600 mt-2">Veuillez entrer le mot de passe pour accéder au système</p>
        </div>

        {}
        {rateLimitInfo?.blocked && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-800">
              <i className="fas fa-exclamation-triangle text-red-600 mr-3 text-xl"></i>
              <div>
                <p className="font-semibold">Trop de tentatives de connexion</p>
                <p className="text-sm mt-1">
                  Veuillez patienter <span className="font-bold">{rateLimitInfo.retryAfter}</span> seconde{rateLimitInfo.retryAfter > 1 ? 's' : ''} avant de réessayer.
                </p>
              </div>
            </div>
          </div>
        )}

        {}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <i className="fas fa-key mr-1"></i> Mot de passe
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="form-input"
              placeholder="Entrez le mot de passe"
              disabled={isLoading || rateLimitInfo?.blocked}
            />
          </div>
          <button
            type="submit"
            disabled={isLoading || !password.trim() || rateLimitInfo?.blocked}
            className={`w-full btn ${rateLimitInfo?.blocked ? 'btn-cancel' : 'btn-primary'}`}
          >
            <i className={`fas ${rateLimitInfo?.blocked ? 'fa-clock' : 'fa-sign-in-alt'} mr-2`}></i>
            {rateLimitInfo?.blocked
              ? `Patientez ${rateLimitInfo.retryAfter}s...`
              : isLoading
                ? 'Connexion...'
                : 'Se connecter'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
